import { useState } from 'react';
import { getDistanceUnit } from '../../utils/units';
import { updateSettings } from '../../utils/storage';
import { isGeolocationSupported, getZipFromLocation } from '../../utils/geolocation';

const LEVELS = [
  { id: 'Beginner', emoji: '\uD83D\uDD27', label: 'Beginner', desc: 'I change air fresheners and check tire pressure' },
  { id: 'Intermediate', emoji: '\uD83D\uDD27\uD83D\uDD27', label: 'Intermediate', desc: 'I do oil changes, filters, and basic maintenance' },
  { id: 'Advanced', emoji: '\uD83D\uDD27\uD83D\uDD27\uD83D\uDD27', label: 'Advanced', desc: 'I can do brakes, suspension, and some engine work' },
  { id: 'Expert', emoji: '\uD83D\uDD27\uD83D\uDD27\uD83D\uDD27\uD83D\uDD27', label: 'Expert', desc: 'I rebuild engines and transmissions' },
];

const UNIT_OPTIONS = ['miles', 'km'];

const currentYear = new Date().getFullYear();

export default function StepLocation({ data, update, onNext, onBack }) {
  const [units, setUnits] = useState(() => getDistanceUnit());
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState(null);
  // Accept US ZIP, US ZIP+4, Canada A1A 1A1, UK postcode (SW1A 1AA), and a
  // permissive 3-10 alphanumeric fallback for the rest of the world.
  // Keep this loose — we only use the postcode to bias map proximity, not
  // to enforce any business logic.
  const zipValid = /^[A-Za-z0-9][A-Za-z0-9 -]{1,9}[A-Za-z0-9]$/.test((data.zipCode || '').trim());
  const canProceed = zipValid && data.mechanicalLevel && data.yearOfBirth;

  function handleUnitChange(unit) {
    setUnits(unit);
    updateSettings({ preferences: { units: unit } });
  }

  function handleZipChange(e) {
    // Strip anything that's not alphanumeric, space, or dash. Uppercase
    // for UK postcode style. Cap length to 10.
    const val = e.target.value.replace(/[^A-Za-z0-9 -]/g, '').slice(0, 10).toUpperCase();
    update({ zipCode: val });
    if (locError) setLocError(null);
  }

  async function handleUseLocation() {
    setLocating(true);
    setLocError(null);
    const { zip, error } = await getZipFromLocation();
    setLocating(false);
    if (zip) {
      update({ zipCode: zip });
    } else {
      setLocError(error);
    }
  }

  return (
    <div className="screen">
      {/* Back */}
      <button
        onClick={onBack}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', color: 'var(--text-secondary)',
          fontSize: '0.875rem', cursor: 'pointer', padding: 0, marginBottom: 8,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      <h1 className="screen-title" style={{ fontSize: '1.25rem', marginBottom: 4 }}>
        Help us personalize your experience
      </h1>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginBottom: 28 }}>
        All fields on this screen are required
      </p>

      {/* Postcode / ZIP */}
      <div className="form-group">
        <label className="form-label">Location</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            className="form-input"
            type="text"
            inputMode="text"
            maxLength={10}
            value={data.zipCode || ''}
            onChange={handleZipChange}
            placeholder="Postcode / ZIP"
            autoComplete="postal-code"
            style={{ flex: 1, textTransform: 'uppercase' }}
          />
          {isGeolocationSupported() && (
            <button
              type="button"
              onClick={handleUseLocation}
              disabled={locating}
              aria-label="Use my location"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '10px 14px',
                background: 'var(--accent)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                color: 'white',
                fontSize: '0.8125rem',
                fontWeight: 600,
                cursor: locating ? 'wait' : 'pointer',
                opacity: locating ? 0.7 : 1,
                whiteSpace: 'nowrap',
                transition: 'opacity 0.2s',
                flexShrink: 0,
              }}
            >
              {locating ? (
                <svg width="16" height="16" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeDasharray="31.4 31.4" strokeLinecap="round" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              )}
              {locating ? 'Locating...' : 'Use Location'}
            </button>
          )}
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', marginTop: 6 }}>
          Used only to bias map searches and suggest nearby clubs/events. We don&rsquo;t store your exact GPS — just the postcode.
        </p>
        {data.zipCode && !zipValid && data.zipCode.length > 0 && (
          <p style={{ color: 'var(--warning)', fontSize: '0.75rem', marginTop: 4 }}>
            Enter a valid postcode or ZIP (3–10 characters).
          </p>
        )}
        {locError && (
          <p style={{ color: 'var(--warning)', fontSize: '0.75rem', marginTop: 4 }}>
            {locError}
          </p>
        )}
      </div>

      {/* Mechanical Level */}
      <div className="form-group">
        <label className="form-label">Your mechanical experience level</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {LEVELS.map((level) => (
            <button
              key={level.id}
              onClick={() => update({ mechanicalLevel: level.id })}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                background: data.mechanicalLevel === level.id ? 'var(--accent-subtle)' : 'var(--bg-input)',
                border: `1.5px solid ${data.mechanicalLevel === level.id ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'var(--text-primary)',
                transition: 'all 0.2s',
              }}
            >
              <span style={{ fontSize: '1.125rem', flexShrink: 0, width: 50 }}>{level.emoji}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{level.label}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{level.desc}</div>
              </div>
              {data.mechanicalLevel === level.id && (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Distance Units */}
      <div className="form-group">
        <label className="form-label">Distance Units</label>
        <div style={{ display: 'flex', borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: '1.5px solid var(--border)' }}>
          {UNIT_OPTIONS.map((unit) => (
            <button
              key={unit}
              type="button"
              onClick={() => handleUnitChange(unit)}
              style={{
                flex: 1,
                padding: '10px 0',
                fontSize: '0.875rem',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                background: units === unit ? 'var(--accent)' : 'var(--bg-input)',
                color: units === unit ? 'white' : 'var(--text-secondary)',
                transition: 'all 0.2s',
              }}
            >
              {unit === 'miles' ? 'Miles' : 'Kilometers'}
            </button>
          ))}
        </div>
      </div>

      {/* Year of Birth (required) */}
      <div className="form-group">
        <label className="form-label">Year of Birth</label>
        <select
          className="form-select"
          value={data.yearOfBirth || ''}
          onChange={(e) => update({ yearOfBirth: e.target.value ? Number(e.target.value) : '' })}
        >
          <option value="">Select year</option>
          {Array.from({ length: currentYear - 1940 - 15 }, (_, i) => currentYear - 18 - i).map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      <div className="nav-row">
        <button className="btn btn-primary" disabled={!canProceed} onClick={onNext}>
          Continue
        </button>
      </div>

      {locating && <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>}
    </div>
  );
}
